/**
 * UI 専用ストア（Composition と無関係な画面状態）。
 * - パネル幅/高さ（localStorage 保持）
 * - 素材ビン ⇄ タイムラインの相互ハイライト用 hoverAssetId
 *
 * 所有: UI MK2 第1段 1b（レイアウト/素材ビン）。1a（タイムライン）は読み取りのみ。
 * Composition の状態はここに置かない（editorStore が正）。
 */
import { create } from 'zustand'

export interface PanelSizes {
  /** 左ペイン（素材ビン）幅 px */
  left: number
  /** 右ペイン（コントロールパネル）幅 px */
  right: number
  /** 下タイムライン高さ px */
  timeline: number
}

export const DEFAULT_PANEL_SIZES: PanelSizes = { left: 300, right: 340, timeline: 260 }

/** min/max。timeline の max は viewport 高さに対する比率（0.5 = 50vh） */
export const PANEL_LIMITS = {
  left: { min: 220, max: 480 },
  right: { min: 280, max: 520 },
  // reviewer 差し戻し M3（1a 側の依頼で 1b 承認済み）: Timeline.tsx の
  // `min-h-[260px]` と食い違っていた（160〜259px の間で音声レーン/通知が
  // 画面外に消える）。260 に揃えて矛盾を解消。
  timeline: { min: 260, maxVh: 0.5 },
} as const

const LS_KEY = 'aquacut.ui.panelSizes.v1'

function loadPanelSizes(): PanelSizes {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return DEFAULT_PANEL_SIZES
    const parsed = JSON.parse(raw) as Partial<PanelSizes>
    return {
      left: clamp(parsed.left ?? DEFAULT_PANEL_SIZES.left, PANEL_LIMITS.left.min, PANEL_LIMITS.left.max),
      right: clamp(parsed.right ?? DEFAULT_PANEL_SIZES.right, PANEL_LIMITS.right.min, PANEL_LIMITS.right.max),
      timeline: Math.max(PANEL_LIMITS.timeline.min, parsed.timeline ?? DEFAULT_PANEL_SIZES.timeline),
    }
  } catch {
    return DEFAULT_PANEL_SIZES
  }
}

function savePanelSizes(sizes: PanelSizes) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(sizes))
  } catch {
    /* private mode 等。無視 */
  }
}

export function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v))
}

export type AssetBinViewMode = 'grid' | 'list'
export type AssetBinSort = 'added' | 'name' | 'duration'

const LS_PREVIEW_MUTED_KEY = 'aquacut.ui.previewMuted.v1'

function loadPreviewMuted(): boolean {
  try {
    return localStorage.getItem(LS_PREVIEW_MUTED_KEY) === '1'
  } catch {
    return false
  }
}

function savePreviewMuted(muted: boolean) {
  try {
    localStorage.setItem(LS_PREVIEW_MUTED_KEY, muted ? '1' : '0')
  } catch {
    /* private mode 等。無視 */
  }
}

const LS_VIEW_KEY = 'aquacut.ui.assetBinViewMode.v1'

function loadAssetBinViewMode(): AssetBinViewMode {
  try {
    const raw = localStorage.getItem(LS_VIEW_KEY)
    return raw === 'list' ? 'list' : 'grid'
  } catch {
    return 'grid'
  }
}

function saveAssetBinViewMode(mode: AssetBinViewMode) {
  try {
    localStorage.setItem(LS_VIEW_KEY, mode)
  } catch {
    /* private mode 等。無視 */
  }
}

interface UiState {
  panelSizes: PanelSizes
  setPanelSize: (key: keyof PanelSizes, px: number) => void
  resetPanelSizes: () => void

  /** 素材ビンでホバー/選択中の asset id。Timeline 側は同 asset の Segment を淡くハイライトする */
  hoverAssetId: string | null
  setHoverAssetId: (id: string | null) => void

  /** 素材ビン: グリッド/リスト表示切替（既定グリッド・localStorage 保持） */
  assetBinViewMode: AssetBinViewMode
  setAssetBinViewMode: (mode: AssetBinViewMode) => void

  /** 素材ビン: 名前部分一致の検索欄（画面遷移で消えて良い・保持しない） */
  assetBinQuery: string
  setAssetBinQuery: (q: string) => void

  /** 素材ビン: 並び替え（追加順/名前/尺） */
  assetBinSort: AssetBinSort
  setAssetBinSort: (s: AssetBinSort) => void

  /**
   * P10-1 担当C: 編集画面プレビューの BGM/SFX ミュート（既定 false・
   * localStorage 保持は assetBinViewMode と同じ方式）。usePreviewAudio.ts が
   * 購読して全 `<audio>` に `muted` を反映する。
   */
  previewMuted: boolean
  setPreviewMuted: (muted: boolean) => void
  togglePreviewMuted: () => void

  /**
   * P10-3 担当A (docs/P10_PHASE3.md §2 item 5): ExportHistory.tsx's
   * 「この構成案／プロジェクト全体」toggle. Deliberately NOT persisted to
   * localStorage (task spec: "localStorage 不要") — resets to 'composition'
   * on reload, unlike every other field above.
   */
  exportHistoryScope: 'composition' | 'project'
  setExportHistoryScope: (scope: 'composition' | 'project') => void
}

export const useUiStore = create<UiState>((set, get) => ({
  panelSizes: loadPanelSizes(),
  setPanelSize: (key, px) => {
    const cur = get().panelSizes
    let next = px
    if (key === 'left') next = clamp(px, PANEL_LIMITS.left.min, PANEL_LIMITS.left.max)
    if (key === 'right') next = clamp(px, PANEL_LIMITS.right.min, PANEL_LIMITS.right.max)
    if (key === 'timeline') {
      const maxPx = typeof window !== 'undefined' ? window.innerHeight * PANEL_LIMITS.timeline.maxVh : 600
      next = clamp(px, PANEL_LIMITS.timeline.min, maxPx)
    }
    const sizes = { ...cur, [key]: Math.round(next) }
    savePanelSizes(sizes)
    set({ panelSizes: sizes })
  },
  resetPanelSizes: () => {
    savePanelSizes(DEFAULT_PANEL_SIZES)
    set({ panelSizes: DEFAULT_PANEL_SIZES })
  },

  hoverAssetId: null,
  setHoverAssetId: (id) => set({ hoverAssetId: id }),

  assetBinViewMode: loadAssetBinViewMode(),
  setAssetBinViewMode: (mode) => {
    saveAssetBinViewMode(mode)
    set({ assetBinViewMode: mode })
  },

  assetBinQuery: '',
  setAssetBinQuery: (q) => set({ assetBinQuery: q }),

  assetBinSort: 'added',
  setAssetBinSort: (s) => set({ assetBinSort: s }),

  previewMuted: loadPreviewMuted(),
  setPreviewMuted: (muted) => {
    savePreviewMuted(muted)
    set({ previewMuted: muted })
  },
  togglePreviewMuted: () => {
    const next = !get().previewMuted
    savePreviewMuted(next)
    set({ previewMuted: next })
  },

  exportHistoryScope: 'composition',
  setExportHistoryScope: (scope) => set({ exportHistoryScope: scope }),
}))
